/**
 * Règle, équerre, compas — logique pure (Boîte à outils, 6e → 3e).
 *
 * Une construction est une LISTE d'objets ; chacun ne dépend que d'objets
 * placés avant lui (points libres, intersections, segments, droites,
 * demi-droites, cercles, perpendiculaires, parallèles). On la réévalue à
 * chaque changement : déplacer un point libre fait suivre toute la figure.
 * Une intersection garde la solution la plus proche de sa position
 * précédente (continuité quand deux cercles se coupent en deux points) ; on
 * ne met cette position à jour que s'il y a bien deux solutions, sinon les
 * deux points d'une paire fusionneraient pour toujours après une tangence.
 *
 * Unité : le centimètre ; repère x vers la droite, y vers le haut.
 */

export interface P {
  x: number;
  y: number;
}

export type LineStyle = 'segment' | 'line' | 'ray';

export type PointDef =
  | { t: 'free'; x: number; y: number }
  | { t: 'inter'; a: number; b: number; near: P };

export type LineDef =
  | { t: '2p'; p: number; q: number; style: LineStyle }
  | { t: 'perp' | 'par'; line: number; p: number };

/** cercle : passant par un point, de rayon donné, ou de rayon reporté (longueur PQ) */
export type CircleDef =
  | { t: 'cp'; c: number; p: number }
  | { t: 'cr'; c: number; r: number }
  | { t: 'cd'; c: number; p: number; q: number };

export type Obj =
  | { id: number; kind: 'point'; name: string; def: PointDef; given?: boolean }
  | { id: number; kind: 'line'; name: string; def: LineDef; given?: boolean }
  | { id: number; kind: 'circle'; name: string; def: CircleDef; given?: boolean };

export interface LineV {
  a: P;
  b: P;
  style: LineStyle;
}
export interface CircleV {
  c: P;
  r: number;
}
export type Val =
  | { kind: 'point'; p: P }
  | { kind: 'line'; l: LineV }
  | { kind: 'circle'; c: CircleV };

const EPS = 1e-9;
export const dist = (a: P, b: P) => Math.hypot(a.x - b.x, a.y - b.y);
const sub = (a: P, b: P): P => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: P, b: P): P => ({ x: a.x + b.x, y: a.y + b.y });
const mul = (a: P, k: number): P => ({ x: a.x * k, y: a.y * k });
const cross = (a: P, b: P) => a.x * b.y - a.y * b.x;
const dot = (a: P, b: P) => a.x * b.x + a.y * b.y;

/** le paramètre t (a + t(b − a)) est-il sur l'objet tracé ? */
function onStyle(style: LineStyle, t: number) {
  if (style === 'segment') return t >= -1e-7 && t <= 1 + 1e-7;
  if (style === 'ray') return t >= -1e-7;
  return true;
}

export function lineLine(l1: LineV, l2: LineV): P[] {
  const d1 = sub(l1.b, l1.a);
  const d2 = sub(l2.b, l2.a);
  const den = cross(d1, d2);
  if (Math.abs(den) < EPS) return [];
  const w = sub(l2.a, l1.a);
  const t = cross(w, d2) / den;
  const u = cross(w, d1) / den;
  if (!onStyle(l1.style, t) || !onStyle(l2.style, u)) return [];
  return [add(l1.a, mul(d1, t))];
}

export function lineCircle(l: LineV, c: CircleV): P[] {
  const d = sub(l.b, l.a);
  const f = sub(l.a, c.c);
  const A = dot(d, d);
  const B = 2 * dot(f, d);
  const C = dot(f, f) - c.r * c.r;
  const disc = B * B - 4 * A * C;
  if (disc < -1e-9 || A < EPS) return [];
  const s = Math.sqrt(Math.max(0, disc));
  const ts = disc < 1e-12 ? [-B / (2 * A)] : [(-B - s) / (2 * A), (-B + s) / (2 * A)];
  return ts.filter((t) => onStyle(l.style, t)).map((t) => add(l.a, mul(d, t)));
}

export function circleCircle(c1: CircleV, c2: CircleV): P[] {
  const d = dist(c1.c, c2.c);
  if (d < EPS || d > c1.r + c2.r + 1e-9 || d < Math.abs(c1.r - c2.r) - 1e-9) return [];
  const a = (c1.r * c1.r - c2.r * c2.r + d * d) / (2 * d);
  const h2 = c1.r * c1.r - a * a;
  const h = Math.sqrt(Math.max(0, h2));
  const u = mul(sub(c2.c, c1.c), 1 / d);
  const m = add(c1.c, mul(u, a));
  const n: P = { x: -u.y, y: u.x };
  return h < 1e-7 ? [m] : [add(m, mul(n, h)), sub(m, mul(n, h))];
}

export function intersect(a: Val, b: Val): P[] {
  if (a.kind === 'line' && b.kind === 'line') return lineLine(a.l, b.l);
  if (a.kind === 'line' && b.kind === 'circle') return lineCircle(a.l, b.c);
  if (a.kind === 'circle' && b.kind === 'line') return lineCircle(b.l, a.c);
  if (a.kind === 'circle' && b.kind === 'circle') return circleCircle(a.c, b.c);
  return [];
}

/** valeurs de tous les objets (null : n'existe plus, ex. deux cercles qui ne se coupent plus) */
export function evaluate(objs: readonly Obj[]): Array<Val | null> {
  const out: Array<Val | null> = [];
  const byId = new Map<number, number>();
  const get = (id: number) => {
    const i = byId.get(id);
    return i === undefined ? null : out[i]!;
  };
  const pt = (id: number) => {
    const v = get(id);
    return v && v.kind === 'point' ? v.p : null;
  };
  objs.forEach((o, i) => {
    byId.set(o.id, i);
    let v: Val | null = null;
    if (o.kind === 'point') {
      const d = o.def;
      if (d.t === 'free') v = { kind: 'point', p: { x: d.x, y: d.y } };
      else {
        const a = get(d.a);
        const b = get(d.b);
        if (a && b) {
          const sols = intersect(a, b);
          if (sols.length) {
            const best = sols.reduce((m, s) => (dist(s, d.near) < dist(m, d.near) ? s : m));
            v = { kind: 'point', p: best };
          }
        }
      }
    } else if (o.kind === 'line') {
      const d = o.def;
      if (d.t === '2p') {
        const p = pt(d.p);
        const q = pt(d.q);
        if (p && q && dist(p, q) > 1e-7) v = { kind: 'line', l: { a: p, b: q, style: d.style } };
      } else {
        const base = get(d.line);
        const p = pt(d.p);
        if (base && base.kind === 'line' && p) {
          const dir = sub(base.l.b, base.l.a);
          const nd = d.t === 'perp' ? { x: -dir.y, y: dir.x } : dir;
          v = { kind: 'line', l: { a: p, b: add(p, nd), style: 'line' } };
        }
      }
    } else {
      const d = o.def;
      const c = pt(d.c);
      if (c) {
        const r =
          d.t === 'cr'
            ? d.r
            : d.t === 'cp'
              ? (() => {
                  const p = pt(d.p);
                  return p ? dist(c, p) : null;
                })()
              : (() => {
                  const p = pt(d.p);
                  const q = pt(d.q);
                  return p && q ? dist(p, q) : null;
                })();
        if (r !== null && r > 1e-7) v = { kind: 'circle', c: { c, r } };
      }
    }
    out.push(v);
  });
  return out;
}

/** met à jour la position de référence des intersections (continuité) */
export function refreshNear(objs: readonly Obj[], vals: ReadonlyArray<Val | null>): Obj[] {
  const index = new Map(objs.map((o, i) => [o.id, i]));
  return objs.map((o, i) => {
    const v = vals[i];
    if (o.kind !== 'point' || o.def.t !== 'inter' || !v || v.kind !== 'point') return o;
    const a = vals[index.get(o.def.a) ?? -1];
    const b = vals[index.get(o.def.b) ?? -1];
    // tangence (une seule solution) : on garde l'ancienne position de référence
    const twoLines = a?.kind === 'line' && b?.kind === 'line';
    if (!a || !b) return o;
    if (!twoLines) {
      const sols = intersect(a, b);
      if (sols.length < 2 || dist(sols[0]!, sols[1]!) < 1e-3) return o;
    }
    return { ...o, def: { ...o.def, near: v.p } };
  });
}

/* ---------- noms et programme de construction ---------- */

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SUB = '₀₁₂₃₄₅₆₇₈₉';
const subscript = (n: number) =>
  String(n)
    .split('')
    .map((c) => SUB[Number(c)])
    .join('');

export function nextPointName(objs: readonly Obj[], prefer?: string): string {
  const used = new Set(objs.filter((o) => o.kind === 'point').map((o) => o.name));
  if (prefer && !used.has(prefer)) return prefer;
  for (const l of LETTERS) if (!used.has(l)) return l;
  for (let k = 1; ; k++) for (const l of LETTERS) if (!used.has(`${l}${k}`)) return `${l}${k}`;
}

export function nextName(objs: readonly Obj[], kind: 'line' | 'circle'): string {
  const n =
    objs.filter((o) => o.kind === kind && !(o.kind === 'line' && o.def.t === '2p')).length + 1;
  return kind === 'line' ? `d${subscript(n)}` : `𝒞${subscript(n)}`;
}

const nameOf = (objs: readonly Obj[], id: number) => objs.find((o) => o.id === id)?.name ?? '?';

/** « [AB] », « (AB) », « [AB) », « (d₁) », « 𝒞₁ » */
export function label(objs: readonly Obj[], o: Obj): string {
  if (o.kind === 'point') return o.name;
  if (o.kind === 'circle') return o.name;
  if (o.def.t === '2p') {
    const p = nameOf(objs, o.def.p);
    const q = nameOf(objs, o.def.q);
    return o.def.style === 'segment'
      ? `[${p}${q}]`
      : o.def.style === 'ray'
        ? `[${p}${q})`
        : `(${p}${q})`;
  }
  return `(${o.name})`;
}

/** la droite qui porte un objet rectiligne : « (AB) » pour [AB] */
function carrier(objs: readonly Obj[], id: number): string {
  const o = objs.find((x) => x.id === id);
  if (!o || o.kind !== 'line') return '?';
  if (o.def.t === '2p') return `(${nameOf(objs, o.def.p)}${nameOf(objs, o.def.q)})`;
  return `(${o.name})`;
}

export function stepText(objs: readonly Obj[], o: Obj): string {
  const L = (id: number) => {
    const x = objs.find((y) => y.id === id);
    return x ? label(objs, x) : '?';
  };
  if (o.kind === 'point') {
    if (o.def.t === 'free') return `Place un point ${o.name}.`;
    // deux droites : LE point d'intersection ; un cercle : l'UN des points
    const kinds = [o.def.a, o.def.b].map((id) => objs.find((y) => y.id === id)?.kind);
    const which = kinds.every((k) => k === 'line') ? 'le point' : 'un point';
    return `Place le point ${o.name}, ${which} d’intersection de ${L(o.def.a)} et ${L(o.def.b)}.`;
  }
  if (o.kind === 'circle') {
    const c = nameOf(objs, o.def.c);
    const d = o.def;
    return d.t === 'cp'
      ? `Trace le cercle ${o.name} de centre ${c} passant par ${nameOf(objs, d.p)}.`
      : d.t === 'cd'
        ? `Trace le cercle ${o.name} de centre ${c} et de rayon ${nameOf(objs, d.p)}${nameOf(objs, d.q)} (écartement reporté au compas).`
        : `Trace le cercle ${o.name} de centre ${c} et de rayon ${String(d.r).replace('.', ',')} cm.`;
  }
  if (o.def.t === '2p') {
    const what =
      o.def.style === 'segment'
        ? 'le segment'
        : o.def.style === 'ray'
          ? 'la demi-droite'
          : 'la droite';
    return `Trace ${what} ${label(objs, o)}.`;
  }
  return `Trace la droite (${o.name}) ${o.def.t === 'perp' ? 'perpendiculaire' : 'parallèle'} à ${carrier(objs, o.def.line)} passant par ${nameOf(objs, o.def.p)}.`;
}

/* ---------- toucher : quel objet est là ? ---------- */

export interface Frame {
  /** cm → écran */
  sx: (x: number) => number;
  sy: (y: number) => number;
  /** écran → cm */
  wx: (px: number) => number;
  wy: (py: number) => number;
  scale: number;
  /** fenêtre en cm */
  xmin: number;
  xmax: number;
  ymin: number;
  ymax: number;
}

export function makeFrame(): Frame {
  const scale = 32;
  const ox = 10;
  const oy = 268;
  return {
    scale,
    sx: (x) => ox + x * scale,
    sy: (y) => oy - y * scale,
    wx: (px) => (px - ox) / scale,
    wy: (py) => (oy - py) / scale,
    xmin: 0,
    xmax: 10,
    ymin: 0,
    ymax: 8,
  };
}

/** points de la droite visibles dans la fenêtre (droite : bords ; demi-droite : origine → bord) */
export function visibleEnds(l: LineV, f: Frame): [P, P] | null {
  if (l.style === 'segment') return [l.a, l.b];
  const d = sub(l.b, l.a);
  const ts: number[] = [];
  const box = { x0: f.xmin - 1, x1: f.xmax + 1, y0: f.ymin - 1, y1: f.ymax + 1 };
  if (Math.abs(d.x) > EPS) for (const x of [box.x0, box.x1]) ts.push((x - l.a.x) / d.x);
  if (Math.abs(d.y) > EPS) for (const y of [box.y0, box.y1]) ts.push((y - l.a.y) / d.y);
  const inside = ts
    .map((t) => ({ t, p: add(l.a, mul(d, t)) }))
    .filter(
      ({ p }) =>
        p.x >= box.x0 - 1e-6 &&
        p.x <= box.x1 + 1e-6 &&
        p.y >= box.y0 - 1e-6 &&
        p.y <= box.y1 + 1e-6,
    )
    .map((x) => x.t);
  if (inside.length < 2) return null;
  let t0 = Math.min(...inside);
  const t1 = Math.max(...inside);
  if (l.style === 'ray') t0 = Math.max(0, t0);
  if (t1 < t0) return null;
  return [add(l.a, mul(d, t0)), add(l.a, mul(d, t1))];
}

/** distance (cm) d'un point à un objet tracé */
export function distTo(v: Val, p: P): number {
  if (v.kind === 'point') return dist(v.p, p);
  if (v.kind === 'circle') return Math.abs(dist(v.c.c, p) - v.c.r);
  const d = sub(v.l.b, v.l.a);
  let t = dot(sub(p, v.l.a), d) / dot(d, d);
  if (v.l.style === 'segment') t = Math.max(0, Math.min(1, t));
  if (v.l.style === 'ray') t = Math.max(0, t);
  return dist(p, add(v.l.a, mul(d, t)));
}

export interface Candidate {
  a: number;
  b: number;
  p: P;
}

/** toutes les intersections possibles entre objets tracés (pour placer un point) */
export function candidates(objs: readonly Obj[], vals: ReadonlyArray<Val | null>): Candidate[] {
  const out: Candidate[] = [];
  const curves = objs.map((o, i) => ({ o, v: vals[i] })).filter((x) => x.v && x.v.kind !== 'point');
  for (let i = 0; i < curves.length; i++)
    for (let j = i + 1; j < curves.length; j++)
      for (const p of intersect(curves[i]!.v!, curves[j]!.v!))
        out.push({ a: curves[i]!.o.id, b: curves[j]!.o.id, p });
  // une intersection déjà placée n'est plus une candidate ; hors de la feuille non plus
  const placed = vals.filter((v): v is { kind: 'point'; p: P } => !!v && v.kind === 'point');
  return out.filter(
    (c) =>
      !placed.some((v) => dist(v.p, c.p) < 1e-6) &&
      c.p.x >= -0.05 &&
      c.p.x <= 10.05 &&
      c.p.y >= -0.05 &&
      c.p.y <= 8.05,
  );
}

/** arrondi au demi-centimètre (points libres) */
export const snapHalf = (v: number) => Math.round(v * 2) / 2;

/* ---------- défis ---------- */

export interface Challenge {
  id: string;
  minRank: number;
  title: string;
  statement: string;
  hint: string;
  given: Obj[];
  /** noms à donner, dans l'ordre, aux points que l'élève construit (« A′ », « O ») */
  names?: string[];
  /** la construction répond-elle ? */
  check: (objs: readonly Obj[], vals: ReadonlyArray<Val | null>) => boolean;
  /** une construction juste, celle de l'indice (pour les tests) */
  solution: () => Obj[];
}

const TOL = 0.03;

const entries = (objs: readonly Obj[], vals: ReadonlyArray<Val | null>) =>
  objs.map((o, i) => ({ o, v: vals[i] ?? null }));

const pointsOf = (objs: readonly Obj[], vals: ReadonlyArray<Val | null>) =>
  entries(objs, vals).filter(
    (x): x is { o: Obj; v: { kind: 'point'; p: P } } => !!x.v && x.v.kind === 'point',
  );

const linesOf = (objs: readonly Obj[], vals: ReadonlyArray<Val | null>) =>
  entries(objs, vals).filter(
    (x): x is { o: Obj; v: { kind: 'line'; l: LineV } } => !!x.v && x.v.kind === 'line',
  );

/** point CONSTRUIT (intersection), pas posé à l'œil */
const isConstructed = (o: Obj) => o.kind === 'point' && o.def.t === 'inter';

/** une droite tracée par l'élève passe par p avec la direction dir */
function lineThrough(
  objs: readonly Obj[],
  vals: ReadonlyArray<Val | null>,
  p: P,
  dir: P,
): Array<{ o: Obj; v: { kind: 'line'; l: LineV } }> {
  return linesOf(objs, vals).filter(({ o, v }) => {
    if (o.given) return false;
    const d = sub(v.l.b, v.l.a);
    const parallel =
      Math.abs(cross(d, dir)) / (Math.hypot(d.x, d.y) * Math.hypot(dir.x, dir.y)) < 0.01;
    const carrierDist = Math.abs(cross(sub(p, v.l.a), d)) / Math.hypot(d.x, d.y);
    return parallel && carrierDist < TOL;
  });
}

/** un segment (ou, si `segmentOnly` est faux, une droite) relie deux points */
function joins(
  objs: readonly Obj[],
  vals: ReadonlyArray<Val | null>,
  p: P,
  q: P,
  segmentOnly = false,
): boolean {
  return linesOf(objs, vals).some(({ v }) => {
    const d = sub(v.l.b, v.l.a);
    const onCarrier = (x: P) => Math.abs(cross(sub(x, v.l.a), d)) / Math.hypot(d.x, d.y) < TOL;
    if (v.l.style === 'segment')
      return (
        (dist(v.l.a, p) < TOL && dist(v.l.b, q) < TOL) ||
        (dist(v.l.a, q) < TOL && dist(v.l.b, p) < TOL)
      );
    return !segmentOnly && onCarrier(p) && onCarrier(q);
  });
}

/** un point CONSTRUIT tombe sur la cible */
const constructedAt = (objs: readonly Obj[], vals: ReadonlyArray<Val | null>, target: P) =>
  pointsOf(objs, vals).find(({ o, v }) => isConstructed(o) && dist(v.p, target) < TOL);

/* fabriques pour les données et les solutions */
let gid = -100;
const fp = (name: string, x: number, y: number): Obj => ({
  id: gid--,
  kind: 'point',
  name,
  def: { t: 'free', x, y },
  given: true,
});
const seg = (p: Obj, q: Obj, style: LineStyle = 'segment'): Obj => ({
  id: gid--,
  kind: 'line',
  name: '',
  def: { t: '2p', p: p.id, q: q.id, style },
  given: true,
});
let sid = 1;
const S = {
  reset: () => {
    sid = 1;
  },
  circle: (def: CircleDef): Obj => ({ id: sid++, kind: 'circle', name: `𝒞${sid}`, def }),
  inter: (a: Obj, b: Obj, near: P, name = 'X'): Obj => ({
    id: sid++,
    kind: 'point',
    name,
    def: { t: 'inter', a: a.id, b: b.id, near },
  }),
  line: (p: Obj, q: Obj, style: LineStyle = 'line'): Obj => ({
    id: sid++,
    kind: 'line',
    name: '',
    def: { t: '2p', p: p.id, q: q.id, style },
  }),
  perp: (line: Obj, p: Obj): Obj => ({
    id: sid++,
    kind: 'line',
    name: 'd₁',
    def: { t: 'perp', line: line.id, p: p.id },
  }),
};

/** centre du cercle circonscrit à trois points */
function circumcenter(a: P, b: P, c: P): P {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  const n = (p: P) => p.x * p.x + p.y * p.y;
  return {
    x: (n(a) * (b.y - c.y) + n(b) * (c.y - a.y) + n(c) * (a.y - b.y)) / d,
    y: (n(a) * (c.x - b.x) + n(b) * (a.x - c.x) + n(c) * (b.x - a.x)) / d,
  };
}

const at = (o: Obj): P => (o.kind === 'point' && o.def.t === 'free' ? o.def : { x: NaN, y: NaN });

function makeChallenges(): Challenge[] {
  const list: Challenge[] = [];
  {
    const A = fp('A', 1, 2);
    const B = fp('B', 8, 4);
    const C = fp('C', 3, 6.5);
    const AB = seg(A, B, 'line');
    list.push({
      id: 'perp',
      minRank: 1,
      title: 'Perpendiculaire',
      statement: 'Trace la droite perpendiculaire à (AB) passant par C.',
      hint: 'L’équerre : choisis la droite (AB), puis le point C.',
      given: [A, B, C, AB],
      check: (o, v) => lineThrough(o, v, at(C), { x: -2, y: 7 }).length > 0,
      solution: () => {
        S.reset();
        return [S.perp(AB, C)];
      },
    });
  }
  {
    // A et B assez proches : les cercles de centre A passant par B, et de centre B passant
    // par A, se recoupent sur la feuille
    const A = fp('A', 3, 3.5);
    const B = fp('B', 6, 4.5);
    const mid = { x: 4.5, y: 4 };
    list.push({
      id: 'mediatrice',
      minRank: 1,
      title: 'Médiatrice au compas',
      statement: 'Trace la médiatrice du segment [AB] avec le compas et la règle.',
      hint: 'Deux cercles de même rayon, de centres A et B, se coupent en deux points : ils sont à égale distance de A et de B. Trace la droite qui passe par ces deux points.',
      given: [A, B, seg(A, B)],
      check: (o, v) =>
        lineThrough(o, v, mid, { x: -1, y: 3 }).some(({ o: l }) => {
          // au compas : la droite passe par deux points CONSTRUITS
          if (l.kind !== 'line' || l.def.t !== '2p') return false;
          const d = l.def;
          return [d.p, d.q].every((id) => {
            const x = o.find((y) => y.id === id);
            return !!x && isConstructed(x);
          });
        }),
      solution: () => {
        S.reset();
        const c1 = S.circle({ t: 'cp', c: A.id, p: B.id });
        const c2 = S.circle({ t: 'cp', c: B.id, p: A.id });
        const M = S.inter(c1, c2, { x: 3.6, y: 6.6 }, 'M');
        const N = S.inter(c1, c2, { x: 5.4, y: 1.4 }, 'N');
        return [c1, c2, M, N, S.line(M, N)];
      },
    });
  }
  {
    const A = fp('A', 2, 1.5);
    const B = fp('B', 8, 1.5);
    list.push({
      id: 'triangle',
      minRank: 1,
      title: 'Triangle de côtés donnés',
      statement: 'AB = 6 cm. Construis un triangle ABC tel que AC = 4 cm et BC = 5 cm.',
      hint: 'Le compas : un cercle de centre A et de rayon 4 cm, un cercle de centre B et de rayon 5 cm. C est l’un de leurs points d’intersection ; trace ensuite [AC] et [BC].',
      given: [A, B, seg(A, B)],
      names: ['C'],
      check: (o, v) =>
        pointsOf(o, v).some(({ o: c, v: cv }) => {
          if (!isConstructed(c)) return false;
          const ok = Math.abs(dist(cv.p, at(A)) - 4) < TOL && Math.abs(dist(cv.p, at(B)) - 5) < TOL;
          return ok && joins(o, v, cv.p, at(A), true) && joins(o, v, cv.p, at(B), true);
        }),
      solution: () => {
        S.reset();
        const c1 = S.circle({ t: 'cr', c: A.id, r: 4 });
        const c2 = S.circle({ t: 'cr', c: B.id, r: 5 });
        const C = S.inter(c1, c2, { x: 4.25, y: 4.8 }, 'C');
        return [c1, c2, C, S.line(A, C, 'segment'), S.line(C, B, 'segment')];
      },
    });
  }
  {
    const E = fp('E', 1, 3);
    const F = fp('F', 9, 5);
    const A = fp('A', 4, 6);
    // symétrique de A par rapport à (EF)
    const d = { x: 8, y: 2 };
    const w = { x: 3, y: 3 };
    const t = dot(w, d) / dot(d, d);
    const H = { x: 1 + d.x * t, y: 3 + d.y * t };
    const target = { x: 2 * H.x - 4, y: 2 * H.y - 6 };
    list.push({
      id: 'symetrique',
      minRank: 1,
      title: 'Symétrique d’un point',
      statement: 'Construis le point A′, symétrique de A par rapport à la droite (EF).',
      hint: 'Au compas : le cercle de centre E passant par A et le cercle de centre F passant par A se recoupent en A′.',
      given: [E, F, A, seg(E, F, 'line')],
      names: ['A′'],
      check: (o, v) => !!constructedAt(o, v, target),
      solution: () => {
        S.reset();
        const c1 = S.circle({ t: 'cp', c: E.id, p: A.id });
        const c2 = S.circle({ t: 'cp', c: F.id, p: A.id });
        return [c1, c2, S.inter(c1, c2, target, 'A′')];
      },
    });
  }
  {
    const A = fp('A', 1, 1.5);
    const B = fp('B', 6, 1.5);
    const C = fp('C', 8.3, 5.7);
    const D = { x: 1 + 8.3 - 6, y: 5.7 };
    list.push({
      id: 'parallelogramme',
      minRank: 2,
      title: 'Parallélogramme',
      statement:
        'Construis le point D tel que ABCD soit un parallélogramme, puis trace [CD] et [DA].',
      hint: 'Au compas (« Reporter une longueur ») : D est à la distance BC de A et à la distance AB de C, car les côtés opposés ont la même longueur. Ou avec des parallèles : à (AB) par C et à (BC) par A.',
      given: [A, B, C, seg(A, B), seg(B, C)],
      names: ['D'],
      check: (o, v) =>
        !!constructedAt(o, v, D) && joins(o, v, D, at(C), true) && joins(o, v, D, at(A), true),
      solution: () => {
        S.reset();
        const c1 = S.circle({ t: 'cd', c: A.id, p: B.id, q: C.id });
        const c2 = S.circle({ t: 'cd', c: C.id, p: A.id, q: B.id });
        const Dp = S.inter(c1, c2, D, 'D');
        return [c1, c2, Dp, S.line(C, Dp, 'segment'), S.line(Dp, A, 'segment')];
      },
    });
  }
  {
    const A = fp('A', 3, 6.5);
    const B = fp('B', 1, 1);
    const C = fp('C', 9, 2);
    const BC = seg(B, C);
    list.push({
      id: 'hauteur',
      minRank: 2,
      title: 'Hauteur d’un triangle',
      statement: 'Trace la hauteur du triangle ABC issue de A.',
      hint: 'La hauteur issue de A passe par A et est perpendiculaire au côté opposé, (BC) : l’équerre.',
      given: [A, B, C, seg(A, B), BC, seg(C, A)],
      check: (o, v) => lineThrough(o, v, at(A), { x: -1, y: 8 }).length > 0,
      solution: () => {
        S.reset();
        return [S.perp(BC, A)];
      },
    });
  }
  {
    // programme de 6e (BO 2025) : construire le cercle circonscrit
    const A = fp('A', 3, 3);
    const B = fp('B', 7, 2.5);
    const C = fp('C', 5.5, 7);
    const O = circumcenter(at(A), at(B), at(C));
    const R = dist(O, at(A));
    list.push({
      id: 'circonscrit',
      minRank: 1,
      title: 'Cercle circonscrit',
      statement: 'Trace le cercle circonscrit au triangle ABC.',
      hint: 'Son centre est à égale distance de A, B et C : c’est le point de concours des médiatrices des côtés. Trace deux médiatrices au compas (« Rayon donné », 3 cm par exemple), puis le cercle de centre leur point d’intersection passant par A.',
      given: [A, B, C, seg(A, B), seg(B, C), seg(C, A)],
      names: [],
      check: (o, v) =>
        entries(o, v).some(({ o: x, v: xv }) => {
          if (x.kind !== 'circle' || !xv || xv.kind !== 'circle') return false;
          const center = o.find((y) => y.id === x.def.c);
          return (
            !!center && isConstructed(center) && dist(xv.c.c, O) < TOL && Math.abs(xv.c.r - R) < TOL
          );
        }),
      solution: () => {
        S.reset();
        const a1 = S.circle({ t: 'cr', c: A.id, r: 3 });
        const b1 = S.circle({ t: 'cr', c: B.id, r: 3 });
        const sols1 = circleCircle({ c: at(A), r: 3 }, { c: at(B), r: 3 });
        const M1 = S.inter(a1, b1, sols1[0]!, 'M');
        const N1 = S.inter(a1, b1, sols1[1]!, 'N');
        const m1 = S.line(M1, N1);
        const b2 = S.circle({ t: 'cr', c: B.id, r: 3 });
        const c2 = S.circle({ t: 'cr', c: C.id, r: 3 });
        const sols2 = circleCircle({ c: at(B), r: 3 }, { c: at(C), r: 3 });
        const M2 = S.inter(b2, c2, sols2[0]!, 'P');
        const N2 = S.inter(b2, c2, sols2[1]!, 'Q');
        const m2 = S.line(M2, N2);
        const Op = S.inter(m1, m2, O, 'O');
        return [
          a1,
          b1,
          M1,
          N1,
          m1,
          b2,
          c2,
          M2,
          N2,
          m2,
          Op,
          S.circle({ t: 'cp', c: Op.id, p: A.id }),
        ];
      },
    });
  }
  return list;
}

export const CHALLENGES: readonly Challenge[] = makeChallenges();
